import React from 'react';
import { Link } from 'react-router-dom';
import {
    Card,
    Descriptions,
    Tabs,
    Table,
    Button,
    Spin,
    message,
    Space,
    Tag,
    Result,
} from 'antd';
import { ArrowLeftOutlined, StarOutlined } from '@ant-design/icons';
import {
    getFundDetail,
    getFundNav,
    getFundHoldings,
    getFundSectors,
    getFundNews,
    addWatchlist,
    removeWatchlist,
    listWatchlist,
} from '../../api/fund';
import { formatReturnRatio, formatNav, formatDateTime } from '../../utils/format';
import { descriptionColumns } from '../../utils/responsive';
import { FUND_PLACEHOLDER_NAME } from '../../constants/fundConstants';
import './FundDetailPage.css';

function parseFundCodeFromPath() {
    const m = window.location.pathname.match(/\/admin\/funds\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : '';
}

class FundDetailPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            code: parseFundCodeFromPath(),
            loading: true,
            accessDenied: false,
            detail: null,
            nav: [],
            holdings: null,
            sectors: null,
            news: [],
            inWatchlist: false,
        };
    }

    componentDidMount() {
        this.loadAll();
    }

    loadAll = async () => {
        const { code } = this.state;
        if (!code) {
            return;
        }
        this.setState({ loading: true, accessDenied: false });
        try {
            const watchlist = await listWatchlist();
            const inWatchlist = watchlist.some((f) => f.fundCode === code);
            if (!inWatchlist) {
                this.setState({ loading: false, accessDenied: true, inWatchlist: false });
                return;
            }
            const [detail, nav, holdings, sectors, news] = await Promise.all([
                getFundDetail(code),
                getFundNav(code),
                getFundHoldings(code),
                getFundSectors(code),
                getFundNews(code),
            ]);
            this.setState({ detail, nav, holdings, sectors, news, inWatchlist: true });
        } catch (e) {
            if (String(e.message || '').includes('关注') || e.status === '403') {
                this.setState({ accessDenied: true });
            }
            message.error(e.message || '加载基金详情失败');
        } finally {
            this.setState({ loading: false });
        }
    };

    handleWatch = async () => {
        const { code, inWatchlist } = this.state;
        try {
            if (inWatchlist) {
                await removeWatchlist(code);
                message.success('已移出关注');
            } else {
                await addWatchlist(code);
                message.success('已加入关注');
            }
            this.setState({ inWatchlist: !inWatchlist, accessDenied: false }, this.loadAll);
        } catch (e) {
            message.error(e.message || '操作失败');
        }
    };

    render() {
        const { code, loading, accessDenied, detail, nav, holdings, sectors, news, inWatchlist } =
            this.state;
        if (!code) {
            return <Card>无效的基金代码</Card>;
        }

        if (accessDenied) {
            return (
                <div className="fund-detail-page">
                    <Card bordered={false}>
                        <Result
                            status="403"
                            title="请先加入关注"
                            subTitle={`基金 ${code} 不在您的关注列表中，加入后可查看详情与分析数据。`}
                            extra={
                                <Space>
                                    <Link to="/admin/funds">
                                        <Button>返回我的基金</Button>
                                    </Link>
                                    <Button type="primary" onClick={this.handleWatch}>
                                        加入关注
                                    </Button>
                                </Space>
                            }
                        />
                    </Card>
                </div>
            );
        }

        const holdingRows = holdings?.holdings || [];
        const sectorRows = sectors?.sectors || holdings?.sectors || [];

        const tabItems = [
            {
                key: 'nav',
                label: '净值',
                children: (
                    <Table
                        size="small"
                        scroll={{ x: 'max-content' }}
                        rowKey={(r) => `${r.tradeDate}-${r.nav}`}
                        pagination={{ pageSize: 15 }}
                        dataSource={nav}
                        columns={[
                            { title: '日期', dataIndex: 'tradeDate' },
                            { title: '单位净值', dataIndex: 'nav', render: formatNav },
                            { title: '累计净值', dataIndex: 'accNav', render: formatNav },
                        ]}
                    />
                ),
            },
            {
                key: 'holdings',
                label: '重仓',
                children: (
                    <>
                        {holdings?.reportDate && (
                            <p>报告期：{holdings.reportDate}</p>
                        )}
                        <Table
                            size="small"
                            scroll={{ x: 'max-content' }}
                            rowKey={(r, i) => `${r.holdingCode}-${i}`}
                            dataSource={holdingRows}
                            pagination={false}
                            columns={[
                                { title: '排名', dataIndex: 'rankNo', width: 60 },
                                { title: '代码', dataIndex: 'holdingCode' },
                                { title: '名称', dataIndex: 'holdingName' },
                                {
                                    title: '占比',
                                    dataIndex: 'weightPct',
                                    render: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '—'),
                                },
                            ]}
                        />
                    </>
                ),
            },
            {
                key: 'sectors',
                label: '行业',
                children: (
                    <Table
                        size="small"
                        scroll={{ x: 'max-content' }}
                        rowKey="sectorName"
                        dataSource={sectorRows}
                        pagination={false}
                        columns={[
                            { title: '行业', dataIndex: 'sectorName' },
                            {
                                title: '占比',
                                dataIndex: 'weightPct',
                                render: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '—'),
                            },
                        ]}
                    />
                ),
            },
            {
                key: 'news',
                label: '资讯',
                children: (
                    <Table
                        size="small"
                        scroll={{ x: 'max-content' }}
                        rowKey="id"
                        dataSource={news}
                        pagination={false}
                        columns={[
                            { title: '标题', dataIndex: 'title', ellipsis: true },
                            { title: '分类', dataIndex: 'categoryName', width: 100 },
                            {
                                title: '时间',
                                dataIndex: 'publishTime',
                                width: 160,
                                render: formatDateTime,
                            },
                        ]}
                    />
                ),
            },
        ];

        return (
            <div className="fund-detail-page">
                <Spin spinning={loading}>
                    <Card
                        bordered={false}
                        title={
                            <Space>
                                <Link to="/admin/funds">
                                    <Button type="text" icon={<ArrowLeftOutlined />} />
                                </Link>
                                <span>
                                    {detail?.fundName || code} ({code})
                                </span>
                                {detail?.fundName === FUND_PLACEHOLDER_NAME && (
                                    <Tag color="orange">待 Agent 同步</Tag>
                                )}
                                {detail?.fundType && <Tag>{detail.fundType}</Tag>}
                            </Space>
                        }
                        extra={
                            <Button icon={<StarOutlined />} onClick={this.handleWatch}>
                                {inWatchlist ? '移出关注' : '加入关注'}
                            </Button>
                        }
                    >
                        {detail && (
                            <>
                                <Descriptions
                                    className="fund-metrics"
                                    size="small"
                                    column={descriptionColumns}
                                    bordered
                                >
                                    <Descriptions.Item label="近1月">
                                        {formatReturnRatio(detail.return1m)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="近3月">
                                        {formatReturnRatio(detail.return3m)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="近1年">
                                        {formatReturnRatio(detail.return1y)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="最大回撤">
                                        {formatReturnRatio(detail.maxDrawdown1y)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="最新净值">
                                        {formatNav(detail.latestNav)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="净值日期">
                                        {detail.latestNavDate || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="基金公司" span={2}>
                                        {detail.fundCompany || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="基金经理">
                                        {detail.fundManager || '—'}
                                    </Descriptions.Item>
                                </Descriptions>
                                {detail.investmentStrategy && (
                                    <p>
                                        <strong>策略：</strong>
                                        {detail.investmentStrategy}
                                    </p>
                                )}
                            </>
                        )}
                        <Tabs items={tabItems} size="small" />
                        {detail?.disclaimer && (
                            <p className="fund-disclaimer">{detail.disclaimer}</p>
                        )}
                    </Card>
                </Spin>
            </div>
        );
    }
}

export default FundDetailPage;
